/**
 * Firma de Aziru dentro del recorrido de demostracion.
 *
 * Aziru es el estudio que construye este sistema. El recorrido lo firma al
 * abrir y al cerrar, igual que una obra lleva la placa de su autor: quien ve la
 * demostracion en la tienda de un cliente tiene que poder saber a quien
 * escribirle para tener la suya.
 *
 * AQUI YA NO VIVE EL DIBUJO, SOLO LA PLACA DEL CIERRE
 * ---------------------------------------------------
 * La geometria de la marca y la palabra se mudaron a
 * `components/AziruMark.tsx` el 25 de agosto de 2026, cuando el MARCO de la
 * aplicacion empezo a firmar con ella —la barra lateral y el encabezado del
 * telefono, o sea las ocho pantallas—. Es el tercer inquilino que hace ese
 * viaje por la misma razon, y las otras dos notas siguen escritas: el glifo de
 * WhatsApp (`components/WhatsAppIcon.tsx`) y el DATO de contacto
 * (`lib/aziru.ts`). Atar el mostrador al modulo de la demostracion es atarlo a
 * algo prescindible.
 *
 * Se importan Y se vuelven a exportar: esta placa los usa adentro y ademas hay
 * quien los importa desde aqui (`DemoScreen`, `chapters`). Un `export ... from`
 * a secas reexporta pero NO trae el simbolo al ambito local, asi que el archivo
 * dejaria de compilar — lo caza `npm run typecheck`.
 *
 * Lo que queda aqui es lo que solo el recorrido usa: la placa de contacto del
 * cierre. El unico color propio es el verde de WhatsApp de su boton, que es
 * marca de un tercero y no un color semantico del sistema.
 */

import { type ReactNode } from 'react';
import { cx } from '../../components/ui.js';
// El glifo se mudo a `components/` cuando la caja empezo a usarlo para mandar el
// ticket a un numero. Importarlo desde aqui habria atado el mostrador al modulo
// de la demostracion.
import { WhatsAppIcon } from '../../components/WhatsAppIcon.js';
import { AziruLockup, AziruMark } from '../../components/AziruMark.js';
export { AziruLockup, AziruMark };

/*
 * El DATO de contacto se mudo a `lib/aziru.ts` cuando la pantalla de Sucursales
 * empezo a necesitarlo para decir a quien contactar al llegar al limite del
 * plan. Es la misma regla que ya movio el glifo de WhatsApp a `components/`:
 * atar el mostrador al modulo de la demostracion es atarlo a algo prescindible.
 */
import { AZIRU } from '../../lib/aziru.js';
export { AZIRU };

// ---------------------------------------------------------------------------
// Contacto
// ---------------------------------------------------------------------------

/**
 * La placa de contacto del cierre: quien hizo el sistema y como pedir el suyo.
 * El boton lleva el verde de WhatsApp porque el boton ES WhatsApp; usar el jade
 * del sistema lo haria pasar por una accion interna de la aplicacion.
 */
export function AziruCard({
  play = true,
  className,
}: {
  play?: boolean;
  className?: string;
}): ReactNode {
  return (
    /* Mas apretada en el telefono: la placa cierra el recorrido y tiene que
       caber completa —boton de WhatsApp incluido— en el alto que deja el
       navegador del celular, sin pedir desplazamiento. */
    <div
      className={cx(
        'rounded-2xl border border-brand-gold/25 bg-surface shadow-float px-5 py-4.5 sm:px-6 sm:py-6 text-center',
        className,
      )}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold">
        Este sistema fue creado por
      </p>

      <AziruLockup
        play={play}
        className="mt-3 sm:mt-4"
        markClassName="h-7 sm:h-8 w-auto"
        wordClassName="text-[1.75rem] sm:text-[2rem]"
      />

      <div aria-hidden className="mx-auto mt-3 sm:mt-4 h-px w-16 bg-brand-gold/50" />

      <p className="mt-3 sm:mt-4 text-[13px] text-muted">{AZIRU.tagline}</p>

      <a
        href={AZIRU.whatsappUrl}
        target="_blank"
        rel="noreferrer"
        className="mt-3.5 sm:mt-5 inline-flex h-11 sm:h-12 items-center justify-center gap-2.5 rounded-full bg-[#25d366] px-6 sm:px-7 text-[15px] font-semibold text-[#07240f] shadow-card transition-[filter] hover:brightness-105"
      >
        <WhatsAppIcon className="size-5" />
        Escribele a {AZIRU.name}
      </a>

      <p className="tabular mt-2.5 sm:mt-3 text-[13px] font-medium text-muted">
        {AZIRU.phoneDisplay}
      </p>
    </div>
  );
}
